import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// The member's calendar through the local server's grant door (TM14 over the
// TM13 lane). The browser names a World and plain calendar fields; the Actor,
// the bearer, and the calendar.manage write gate live server-side in the
// grant. Every failure arrives as the lane's typed refusal envelope and is
// surfaced as `{ kind, reason }` — never thrown, never retried here.

export interface CalendarInstance {
  readonly calendarEventId: string;
  readonly originalStartAt: number;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly title: string;
  readonly timeZone: string;
  readonly recurring: boolean;
  readonly overridden: boolean;
  /** TM17: projected rows (e.g. bookings) arrive read-only from the lane. */
  readonly readonly?: boolean;
  readonly source?: string;
  /** TM22/TM24: a projected booking names itself, this member's side, and the revision read. */
  readonly bookingId?: string;
  readonly bookingRole?: "host" | "attendee";
  readonly bookingRevision?: number;
}

export interface CalendarLaneRefusal {
  readonly ok: false;
  readonly kind: string;
  readonly reason: string;
}

export interface BookingConflictCalendar {
  readonly id: string;
  readonly name: string;
  readonly primary: boolean;
}
const conflictCalendar = (value: unknown): value is BookingConflictCalendar => {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return (
    typeof row.id === "string" && typeof row.name === "string" && typeof row.primary === "boolean"
  );
};
export async function readBookingCalendars(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<
  | CalendarLaneRefusal
  | {
      readonly ok: true;
      readonly calendars: ReadonlyArray<BookingConflictCalendar>;
      readonly revision: number;
      readonly connected: boolean;
      readonly canList: boolean;
    }
> {
  const result = await call("/api/neucleos/booking/calendars", { worldKey }, fetchImpl);
  if ("ok" in result) return result;
  const body = result.body;
  if (
    !Array.isArray(body?.calendars) ||
    !body.calendars.every(conflictCalendar) ||
    typeof body.revision !== "number" ||
    typeof body.connected !== "boolean" ||
    typeof body.canList !== "boolean"
  )
    return { ok: false, kind: "transport", reason: "Selected calendars could not be read." };
  return {
    ok: true,
    calendars: body.calendars,
    revision: body.revision,
    connected: body.connected,
    canList: body.canList,
  };
}
export async function listBookingCalendars(
  worldKey: string,
  cursor?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<
  | CalendarLaneRefusal
  | {
      readonly ok: true;
      readonly calendars: ReadonlyArray<BookingConflictCalendar>;
      readonly cursor: string | null;
      readonly credentialGeneration: number;
    }
> {
  const result = await call(
    "/api/neucleos/booking/calendar-options",
    { worldKey, ...(cursor ? { cursor } : {}) },
    fetchImpl,
  );
  if ("ok" in result) return result;
  const body = result.body;
  if (
    !Array.isArray(body?.calendars) ||
    !body.calendars.every(conflictCalendar) ||
    (body.cursor !== null && typeof body.cursor !== "string") ||
    typeof body.credentialGeneration !== "number"
  )
    return { ok: false, kind: "transport", reason: "Calendar choices could not be read." };
  return {
    ok: true,
    calendars: body.calendars,
    cursor: body.cursor,
    credentialGeneration: body.credentialGeneration,
  };
}
export async function setBookingCalendars(
  worldKey: string,
  calendars: ReadonlyArray<BookingConflictCalendar>,
  expectedRevision: number,
  credentialGeneration: number,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarLaneRefusal | { readonly ok: true; readonly revision: number }> {
  const result = await call(
    "/api/neucleos/booking/set-calendars",
    { worldKey, calendars, expectedRevision, credentialGeneration },
    fetchImpl,
  );
  if ("ok" in result) return result;
  if (typeof result.body?.revision !== "number")
    return { ok: false, kind: "transport", reason: "Selected calendars could not be saved." };
  return { ok: true, revision: result.body.revision };
}

export type CalendarInstancesOutcome =
  | { readonly ok: true; readonly instances: ReadonlyArray<CalendarInstance> }
  | CalendarLaneRefusal;

export type CalendarCreateOutcome =
  | { readonly ok: true; readonly calendarEventId: string }
  | CalendarLaneRefusal;

export interface CalendarCreateInput {
  readonly title: string;
  readonly timeZone: string;
  readonly startsAt: number;
  readonly durationMinutes: number;
  readonly recurrence: string | null;
}

export type CalendarOverrideOutcome = { readonly ok: true } | CalendarLaneRefusal;

export interface CalendarOverrideInput {
  readonly calendarEventId: string;
  readonly originalStartAt: number;
  readonly startsAt: number;
  readonly durationMinutes?: number;
  /** W-84e9c03c: an edit from the event details may rename the occurrence. */
  readonly title?: string;
}

export type CalendarExcludeOutcome = { readonly ok: true } | CalendarLaneRefusal;

export interface CalendarExcludeInput {
  readonly calendarEventId: string;
  readonly originalStartAt: number;
}

function refusal(response: Response, body: Record<string, unknown> | null): CalendarLaneRefusal {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string" ? body["reason"] : "The World calendar is unavailable.",
  };
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<
  | { readonly response: Response; readonly body: Record<string, unknown> | null }
  | CalendarLaneRefusal
> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  return { response, body };
}

export { call as callCalendarDoor };

function parsedInstance(value: unknown): CalendarInstance | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row["calendarEventId"] !== "string" || typeof row["title"] !== "string") return null;
  if (typeof row["timeZone"] !== "string") return null;
  for (const key of ["originalStartAt", "startsAt", "endsAt"]) {
    if (typeof row[key] !== "number" || !Number.isFinite(row[key])) return null;
  }
  return {
    calendarEventId: row["calendarEventId"],
    originalStartAt: row["originalStartAt"] as number,
    startsAt: row["startsAt"] as number,
    endsAt: row["endsAt"] as number,
    title: row["title"],
    timeZone: row["timeZone"],
    recurring: row["recurring"] === true,
    overridden: row["overridden"] === true,
    ...(typeof row["readonly"] === "boolean" ? { readonly: row["readonly"] } : {}),
    ...(typeof row["source"] === "string" ? { source: row["source"] } : {}),
    ...(typeof row["bookingId"] === "string" ? { bookingId: row["bookingId"] } : {}),
    ...(row["bookingRole"] === "host" || row["bookingRole"] === "attendee"
      ? { bookingRole: row["bookingRole"] }
      : {}),
    ...(typeof row["bookingRevision"] === "number"
      ? { bookingRevision: row["bookingRevision"] }
      : {}),
  };
}

export async function listCalendarInstances(
  worldKey: string,
  window: { readonly from: number; readonly to: number },
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarInstancesOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/instances",
    { worldKey, from: window.from, to: window.to },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["instances"];
  if (!Array.isArray(rows)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The calendar lane returned an invalid window.",
    };
  }
  const instances: CalendarInstance[] = [];
  for (const row of rows) {
    const instance = parsedInstance(row);
    if (instance === null) {
      return {
        ok: false,
        kind: "transport",
        reason: "The calendar lane returned an invalid occurrence.",
      };
    }
    instances.push(instance);
  }
  return { ok: true, instances };
}

export async function createCalendarEvent(
  worldKey: string,
  input: CalendarCreateInput,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarCreateOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/create",
    {
      worldKey,
      title: input.title,
      timeZone: input.timeZone,
      startsAt: input.startsAt,
      durationMinutes: input.durationMinutes,
      ...(input.recurrence === null ? {} : { recurrence: input.recurrence }),
    },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const calendarEventId = outcome.body?.["calendarEventId"];
  if (typeof calendarEventId !== "string") {
    return {
      ok: false,
      kind: "transport",
      reason: "The calendar lane returned an invalid event id.",
    };
  }
  return { ok: true, calendarEventId };
}

export async function overrideCalendarInstance(
  worldKey: string,
  input: CalendarOverrideInput,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarOverrideOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/override",
    {
      worldKey,
      calendarEventId: input.calendarEventId,
      originalStartAt: input.originalStartAt,
      startsAt: input.startsAt,
      ...(input.durationMinutes === undefined ? {} : { durationMinutes: input.durationMinutes }),
      ...(input.title === undefined ? {} : { title: input.title }),
    },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

/**
 * W-84e9c03c: Delete from the event details. One occurrence leaves the
 * calendar (for a single event, the event itself) over the lane's existing
 * exclude route — the same calendar.manage door the override rides.
 */
export async function excludeCalendarInstance(
  worldKey: string,
  input: CalendarExcludeInput,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarExcludeOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/exclude",
    {
      worldKey,
      calendarEventId: input.calendarEventId,
      originalStartAt: input.originalStartAt,
    },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

export type CalendarFeedMintOutcome =
  | { readonly ok: true; readonly calendarFeedId: string; readonly feedUrl: string }
  | CalendarLaneRefusal;

export async function mintCalendarFeed(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarFeedMintOutcome> {
  const outcome = await call("/api/neucleos/calendar/feed/mint", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const calendarFeedId = outcome.body?.["calendarFeedId"];
  const feedUrl = outcome.body?.["feedUrl"];
  if (typeof calendarFeedId !== "string" || typeof feedUrl !== "string") {
    return { ok: false, kind: "transport", reason: "The calendar lane returned an invalid feed." };
  }
  return { ok: true, calendarFeedId, feedUrl };
}

export type CalendarFeedRevokeOutcome = { readonly ok: true } | CalendarLaneRefusal;

export async function revokeCalendarFeed(
  worldKey: string,
  calendarFeedId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarFeedRevokeOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/feed/revoke",
    { worldKey, calendarFeedId },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

export interface CalendarSubscription {
  readonly subscriptionId: string;
  readonly url: string;
  readonly name: string;
  readonly accentHue: number | null;
  readonly lastFetchAt: number | null;
  readonly lastFetchError: string | null;
  /** W-c5ddfff5: the person chose this calendar to block appointment times. Absent from an older backend: not chosen. */
  readonly blocksAppointments?: boolean;
  /** Events the last read could not place (zone, repeat rule or time unreadable). */
  readonly unplacedEventCount?: number;
}

export type CalendarSubscriptionListOutcome =
  | { readonly ok: true; readonly subscriptions: ReadonlyArray<CalendarSubscription> }
  | CalendarLaneRefusal;

export async function listCalendarSubscriptions(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarSubscriptionListOutcome> {
  const outcome = await call("/api/neucleos/calendar/subscriptions/list", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["subscriptions"];
  if (!Array.isArray(rows)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The subscription lane returned an invalid list.",
    };
  }
  return { ok: true, subscriptions: rows as CalendarSubscription[] };
}

export type CalendarSubscribeOutcome =
  | { readonly ok: true; readonly subscription: CalendarSubscription }
  | CalendarLaneRefusal;

export async function subscribeCalendarFeed(
  worldKey: string,
  input: { readonly url: string; readonly name: string; readonly accentHue?: number },
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarSubscribeOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/subscriptions/subscribe",
    { worldKey, ...input },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const subscriptionId = outcome.body?.["subscriptionId"];
  if (typeof subscriptionId !== "string") {
    return {
      ok: false,
      kind: "transport",
      reason: "The subscription lane returned an invalid row.",
    };
  }
  return { ok: true, subscription: outcome.body as unknown as CalendarSubscription };
}

export type CalendarSubscriptionActionOutcome = { readonly ok: true } | CalendarLaneRefusal;

export async function removeCalendarSubscription(
  worldKey: string,
  subscriptionId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarSubscriptionActionOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/subscriptions/remove",
    { worldKey, subscriptionId },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

export async function refreshCalendarSubscription(
  worldKey: string,
  subscriptionId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarSubscriptionActionOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/subscriptions/refresh",
    { worldKey, subscriptionId },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

export type CalendarSubscriptionBlocksOutcome =
  | { readonly ok: true; readonly subscription: CalendarSubscription }
  | CalendarLaneRefusal;

/** W-c5ddfff5: choose (or stop choosing) one subscribed calendar to block appointment times. */
export async function setCalendarSubscriptionBlocksAppointments(
  worldKey: string,
  subscriptionId: string,
  blocks: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<CalendarSubscriptionBlocksOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/subscriptions/blocks",
    { worldKey, subscriptionId, blocks },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  if (typeof outcome.body?.["subscriptionId"] !== "string") {
    return {
      ok: false,
      kind: "transport",
      reason: "The subscription lane returned an invalid row.",
    };
  }
  return { ok: true, subscription: outcome.body as unknown as CalendarSubscription };
}

export interface SubscriptionInstance extends CalendarInstance {
  readonly subscriptionId: string;
  readonly subscriptionName: string;
  readonly sourceUid: string;
}

export type SubscriptionInstancesOutcome =
  | { readonly ok: true; readonly instances: ReadonlyArray<SubscriptionInstance> }
  | CalendarLaneRefusal;

export async function listSubscriptionInstances(
  worldKey: string,
  window: { readonly from: number; readonly to: number },
  fetchImpl: typeof fetch = fetch,
): Promise<SubscriptionInstancesOutcome> {
  const outcome = await call(
    "/api/neucleos/calendar/subscriptions/instances",
    { worldKey, from: window.from, to: window.to },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["instances"];
  if (!Array.isArray(rows)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The subscription lane returned an invalid window.",
    };
  }
  return {
    ok: true,
    instances: (rows as Array<Record<string, unknown>>).map((row) => ({
      calendarEventId: `subscription:${String(row["subscriptionId"])}:${String(row["sourceUid"])}`,
      originalStartAt: Number(row["originalStartAt"]),
      startsAt: Number(row["startsAt"]),
      endsAt: Number(row["endsAt"]),
      title: String(row["title"]),
      timeZone: String(row["timeZone"]),
      recurring: row["recurring"] === true,
      overridden: row["overridden"] === true,
      subscriptionId: String(row["subscriptionId"]),
      subscriptionName: String(row["subscriptionName"]),
      sourceUid: String(row["sourceUid"]),
    })),
  };
}

export interface BookingAvailabilityWindow {
  readonly weekday: number;
  readonly startMinute: number;
  readonly endMinute: number;
}

export type BookingAvailabilityOutcome =
  | { readonly ok: true; readonly windows: ReadonlyArray<BookingAvailabilityWindow> }
  | CalendarLaneRefusal;

export async function readBookingAvailability(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<BookingAvailabilityOutcome> {
  const outcome = await call("/api/neucleos/booking/availability", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["windows"];
  if (!Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The booking lane returned invalid windows." };
  }
  return { ok: true, windows: rows as BookingAvailabilityWindow[] };
}

export type BookingAvailabilitySetOutcome =
  | { readonly ok: true; readonly windowCount: number }
  | CalendarLaneRefusal;

export async function setBookingAvailability(
  worldKey: string,
  windows: ReadonlyArray<BookingAvailabilityWindow>,
  fetchImpl: typeof fetch = fetch,
): Promise<BookingAvailabilitySetOutcome> {
  const outcome = await call(
    "/api/neucleos/booking/set-availability",
    { worldKey, windows },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const windowCount = outcome.body?.["windowCount"];
  if (typeof windowCount !== "number") {
    return { ok: false, kind: "transport", reason: "The booking lane returned an invalid count." };
  }
  return { ok: true, windowCount };
}

export interface BookingSchedulingPolicy {
  readonly bufferBeforeMinutes: number;
  readonly bufferAfterMinutes: number;
  readonly minimumNoticeMinutes: number | null;
  readonly horizonDays: number | null;
  readonly slotGridMinutes: number | null;
  readonly maxPerDay: number | null;
}

export interface HostBookingType {
  readonly bookingTypeId: string;
  readonly bookingPageId: string | null;
  readonly title: string;
  readonly durationMinutes: number;
  readonly timeZone: string;
  readonly active: boolean;
  readonly bufferBeforeMinutes?: number;
  readonly bufferAfterMinutes?: number;
  readonly rules?: Pick<
    BookingSchedulingPolicy,
    "minimumNoticeMinutes" | "horizonDays" | "slotGridMinutes" | "maxPerDay"
  >;
  /** The standing video link sent with this type's bookings; the host's own list only. */
  readonly meetingUrl?: string | null;
}

export async function setBookingTypeActive(
  worldKey: string,
  bookingTypeId: string,
  active: boolean,
  fetchImpl: typeof fetch = fetch,
): Promise<{ readonly ok: true } | CalendarLaneRefusal> {
  const outcome = await call(
    "/api/neucleos/booking/set-type-active",
    { worldKey, bookingTypeId, active },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  if (outcome.body?.["bookingTypeId"] !== bookingTypeId || outcome.body?.["active"] !== active) {
    return {
      ok: false,
      kind: "transport",
      reason: "The booking lane did not confirm the requested page state. Refresh to check it.",
    };
  }
  return { ok: true };
}

export async function setBookingTypeRules(
  worldKey: string,
  bookingTypeId: string,
  policy: BookingSchedulingPolicy,
  fetchImpl: typeof fetch = fetch,
): Promise<{ readonly ok: true } | CalendarLaneRefusal> {
  const outcome = await call(
    "/api/neucleos/booking/set-type-rules",
    { worldKey, bookingTypeId, ...policy },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const body = outcome.body;
  const rules = body?.["rules"] as Record<string, unknown> | undefined;
  if (
    body?.["bookingTypeId"] !== bookingTypeId ||
    body?.["bufferBeforeMinutes"] !== policy.bufferBeforeMinutes ||
    body?.["bufferAfterMinutes"] !== policy.bufferAfterMinutes ||
    !rules ||
    rules["minimumNoticeMinutes"] !== policy.minimumNoticeMinutes ||
    rules["horizonDays"] !== policy.horizonDays ||
    rules["slotGridMinutes"] !== policy.slotGridMinutes ||
    rules["maxPerDay"] !== policy.maxPerDay
  ) {
    return {
      ok: false,
      kind: "transport",
      reason: "The booking lane did not confirm the requested policy. Refresh to check it.",
    };
  }
  return { ok: true };
}

export type BookingTypesOutcome =
  | { readonly ok: true; readonly bookingTypes: ReadonlyArray<HostBookingType> }
  | CalendarLaneRefusal;

export async function listBookingTypes(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<BookingTypesOutcome> {
  const outcome = await call("/api/neucleos/booking/types", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["bookingTypes"];
  if (!Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The booking lane returned an invalid list." };
  }
  return { ok: true, bookingTypes: rows as HostBookingType[] };
}

export type BookingTypeCreateOutcome =
  | { readonly ok: true; readonly bookingTypeId: string; readonly bookingPageId: string }
  | CalendarLaneRefusal;

export async function createBookingType(
  worldKey: string,
  input: {
    readonly title: string;
    readonly durationMinutes: number;
    readonly timeZone: string;
    readonly locationKind: string;
    readonly active: boolean;
    readonly description?: string;
  } & Partial<BookingSchedulingPolicy>,
  fetchImpl: typeof fetch = fetch,
): Promise<BookingTypeCreateOutcome> {
  const outcome = await call(
    "/api/neucleos/booking/create-type",
    { worldKey, ...input },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const bookingPageId = outcome.body?.["bookingPageId"];
  const bookingTypeId = outcome.body?.["bookingTypeId"];
  if (typeof bookingPageId !== "string" || typeof bookingTypeId !== "string") {
    return { ok: false, kind: "transport", reason: "The booking lane returned an invalid page." };
  }
  return { ok: true, bookingTypeId, bookingPageId };
}

/** TM22: one booking this member hosts, as the host's own list carries it. */
export interface HostBooking {
  readonly bookingId: string;
  readonly title: string;
  readonly startsAt: number;
  readonly durationMinutes: number;
  readonly status: "confirmed" | "cancelled";
  readonly revision: number;
  readonly inviteeName: string;
  readonly inviteeEmail: string;
  readonly inviteeTimeZone: string;
  readonly signedInAttendee: boolean;
}

/** TM24: one appointment this member booked with a host. */
export interface ConsultingChangePolicy {
  readonly termsRevision: number;
  readonly heldMinutes: number;
  readonly reservedMinutes: number;
  readonly lateCancelNoticeMinutes: number;
  readonly lateCancelChargeMinutes: number;
  readonly lateRescheduleNoticeMinutes: number;
  readonly lateRescheduleChargeMinutes: number;
}
const validChangePolicy = (policy: unknown): policy is ConsultingChangePolicy => {
  if (!policy || typeof policy !== "object" || Array.isArray(policy)) return false;
  const row = policy as Record<string, unknown>;
  return (
    [
      "termsRevision",
      "heldMinutes",
      "reservedMinutes",
      "lateCancelNoticeMinutes",
      "lateCancelChargeMinutes",
      "lateRescheduleNoticeMinutes",
      "lateRescheduleChargeMinutes",
    ].every(
      (key) => typeof row[key] === "number" && Number.isSafeInteger(row[key]) && row[key] >= 0,
    ) && Number(row.termsRevision) > 0
  );
};

export interface MemberAppointment {
  readonly bookingId: string;
  readonly bookingPageId: string;
  readonly title: string;
  readonly hostName: string;
  readonly startsAt: number;
  readonly durationMinutes: number;
  readonly status: "confirmed" | "cancelled";
  readonly revision: number;
  readonly consultingChangePolicy?: ConsultingChangePolicy;
}

function isBookingRow(row: unknown): row is Record<string, unknown> {
  if (typeof row !== "object" || row === null) return false;
  const value = row as Record<string, unknown>;
  return (
    typeof value["bookingId"] === "string" &&
    typeof value["title"] === "string" &&
    typeof value["startsAt"] === "number" &&
    typeof value["durationMinutes"] === "number" &&
    typeof value["revision"] === "number" &&
    (value["status"] === "confirmed" || value["status"] === "cancelled")
  );
}

export type HostBookingsOutcome =
  | { readonly ok: true; readonly bookings: ReadonlyArray<HostBooking> }
  | CalendarLaneRefusal;

export async function listHostBookings(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HostBookingsOutcome> {
  const outcome = await call("/api/neucleos/booking/host-bookings", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["bookings"];
  if (!Array.isArray(rows) || !rows.every(isBookingRow)) {
    return { ok: false, kind: "transport", reason: "The booking lane returned an invalid list." };
  }
  return { ok: true, bookings: rows as unknown as HostBooking[] };
}

export type MemberAppointmentsOutcome =
  | { readonly ok: true; readonly appointments: ReadonlyArray<MemberAppointment> }
  | CalendarLaneRefusal;

export async function listMyAppointments(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<MemberAppointmentsOutcome> {
  const outcome = await call("/api/neucleos/booking/my-appointments", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const rows = outcome.body?.["appointments"];
  if (
    !Array.isArray(rows) ||
    !rows.every(
      (row) =>
        isBookingRow(row) &&
        (row.consultingChangePolicy === undefined || validChangePolicy(row.consultingChangePolicy)),
    )
  ) {
    return { ok: false, kind: "transport", reason: "The booking lane returned an invalid list." };
  }
  return { ok: true, appointments: rows as unknown as MemberAppointment[] };
}

export type BookingChangeOutcome =
  | { readonly ok: true; readonly revision: number; readonly replay: boolean }
  | CalendarLaneRefusal;

/**
 * One change to one booking, fenced by the revision the person was looking
 * at. The lane's echo is checked: a response that does not name this booking
 * at a later revision is not reported as done.
 */
async function changeBooking(
  path: string,
  payload: Record<string, unknown> & {
    readonly bookingId: string;
    readonly expectedRevision: number;
  },
  fetchImpl: typeof fetch,
): Promise<BookingChangeOutcome> {
  const outcome = await call(path, payload, fetchImpl);
  if ("kind" in outcome) return outcome;
  const revision = outcome.body?.["revision"];
  if (
    outcome.body?.["bookingId"] !== payload.bookingId ||
    typeof revision !== "number" ||
    revision <= payload.expectedRevision
  ) {
    return {
      ok: false,
      kind: "transport",
      reason: "The booking lane did not confirm the change. Refresh to check the booking.",
    };
  }
  return { ok: true, revision, replay: outcome.body?.["replay"] === true };
}

export const hostRescheduleBooking = (
  worldKey: string,
  input: {
    readonly bookingId: string;
    readonly expectedRevision: number;
    readonly startsAt: number;
  },
  fetchImpl: typeof fetch = fetch,
) => changeBooking("/api/neucleos/booking/host-reschedule", { worldKey, ...input }, fetchImpl);

export const hostCancelBooking = (
  worldKey: string,
  input: {
    readonly bookingId: string;
    readonly expectedRevision: number;
    readonly reason?: string;
  },
  fetchImpl: typeof fetch = fetch,
) => changeBooking("/api/neucleos/booking/host-cancel", { worldKey, ...input }, fetchImpl);

export const rescheduleMyAppointment = (
  worldKey: string,
  input: {
    readonly bookingId: string;
    readonly expectedRevision: number;
    readonly startsAt: number;
    readonly acceptLateRescheduleChargeMinutes?: number;
  },
  fetchImpl: typeof fetch = fetch,
) => changeBooking("/api/neucleos/booking/my-reschedule", { worldKey, ...input }, fetchImpl);

export const cancelMyAppointment = (
  worldKey: string,
  input: {
    readonly bookingId: string;
    readonly expectedRevision: number;
    readonly reason?: string;
    readonly acceptLateCancelChargeMinutes?: number;
  },
  fetchImpl: typeof fetch = fetch,
) => changeBooking("/api/neucleos/booking/my-cancel", { worldKey, ...input }, fetchImpl);

export interface GoogleCalendarConnectionStatus {
  readonly status: "unconnected" | "active" | "revoking" | "disconnected";
  readonly googleSubject: string | null;
  /** The connected Google account's email; null on connections consented before it was asked. */
  readonly googleEmail: string | null;
  readonly scopes: ReadonlyArray<string>;
  /** While revoking: why Google's revocation last failed, or null. */
  readonly revokeError: string | null;
}

export type GoogleCalendarStatusOutcome =
  | ({ readonly ok: true } & GoogleCalendarConnectionStatus)
  | CalendarLaneRefusal;

export async function readGoogleCalendarStatus(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleCalendarStatusOutcome> {
  const outcome = await call("/api/neucleos/google/status", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const status = outcome.body?.["status"];
  if (typeof status !== "string") {
    return { ok: false, kind: "transport", reason: "The google lane returned an invalid status." };
  }
  return {
    ok: true,
    status: status as GoogleCalendarConnectionStatus["status"],
    googleSubject:
      typeof outcome.body?.["googleSubject"] === "string"
        ? (outcome.body["googleSubject"] as string)
        : null,
    googleEmail:
      typeof outcome.body?.["googleEmail"] === "string"
        ? (outcome.body["googleEmail"] as string)
        : null,
    scopes: Array.isArray(outcome.body?.["scopes"]) ? (outcome.body["scopes"] as string[]) : [],
    revokeError:
      typeof outcome.body?.["revokeError"] === "string"
        ? (outcome.body["revokeError"] as string)
        : null,
  };
}

export type GoogleCalendarDisconnectOutcome = { readonly ok: true } | CalendarLaneRefusal;

export async function disconnectGoogleCalendar(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleCalendarDisconnectOutcome> {
  const outcome = await call("/api/neucleos/google/disconnect", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

export interface GoogleEventInstance {
  readonly sourceEventId: string;
  readonly title: string;
  readonly startsAt: number;
  readonly endsAt: number;
}

export type GoogleEventInstancesOutcome =
  | {
      readonly ok: true;
      readonly connected: boolean;
      readonly readable: boolean;
      readonly eventsFetchError: string | null;
      readonly instances: ReadonlyArray<GoogleEventInstance>;
    }
  | CalendarLaneRefusal;

export async function listGoogleEventInstances(
  worldKey: string,
  window: { readonly from: number; readonly to: number },
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleEventInstancesOutcome> {
  const outcome = await call(
    "/api/neucleos/google/instances",
    { worldKey, from: window.from, to: window.to },
    fetchImpl,
  );
  if ("kind" in outcome) return outcome;
  const connected = outcome.body?.["connected"] === true;
  return {
    ok: true,
    connected,
    readable: outcome.body?.["readable"] === true,
    eventsFetchError:
      typeof outcome.body?.["eventsFetchError"] === "string"
        ? (outcome.body["eventsFetchError"] as string)
        : null,
    instances: Array.isArray(outcome.body?.["instances"])
      ? (outcome.body["instances"] as GoogleEventInstance[])
      : [],
  };
}

export type GoogleRefreshOutcome = { readonly ok: true } | CalendarLaneRefusal;

export async function refreshGoogleEvents(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleRefreshOutcome> {
  const outcome = await call("/api/neucleos/google/refresh", { worldKey }, fetchImpl);
  if ("kind" in outcome) return outcome;
  return { ok: true };
}

export type GoogleBeginOutcome =
  | { readonly ok: true; readonly authorizeUrl: string }
  | CalendarLaneRefusal;

export async function beginGoogleCalendarConnect(
  worldKey: string,
  redirectUri: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GoogleBeginOutcome> {
  const outcome = await call("/api/neucleos/google/begin", { worldKey, redirectUri }, fetchImpl);
  if ("kind" in outcome) return outcome;
  const authorizeUrl = outcome.body?.["authorizeUrl"];
  if (typeof authorizeUrl !== "string") {
    return { ok: false, kind: "transport", reason: "The google lane returned no authorize URL." };
  }
  return { ok: true, authorizeUrl };
}
